import { useCallback } from "react";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { useGetPredictions } from "./useGetPredictions";
import {
    useMediaStore,
    usePromptLabels,
    useAddToInferenceQueue,
    useUpdateInferenceQueueItemStatus,
    useStore,
    InferenceStatus,
    InferenceQueueItem,
    AnnotationsForFileAndFrameId
} from "@rapid/store";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { MediaItem } from "@rapid/types/media";
import {
    buildBoxPromptingDataForLabels,
    getPromptFrameIds,
    waitForPromptFrameUploads
} from "./useAutoPredictions/utils";
import { RAPID_PREVIEW_MIN_CONFIDENCE_THRESHOLD } from "@rapid/utils/predictions";

// ============================================================================
// Types
// ============================================================================

export interface RunInferenceParams {
    /** The media item to run inference on */
    mediaItem: MediaItem;
    /** For videos: metadata about the extracted frame */
    extractedFrameMeta?: { videoId: string; segment: number };
    /** Whether predictions should be marked as draft */
    isDraft?: boolean;
    /** Annotations data - required for building box prompting data */
    annotationsForFileAndFrameId: AnnotationsForFileAndFrameId;
    /** Optional abort controller for cancellation */
    abortController?: AbortController;
    /** Optional custom labels for Rapid text prompting. */
    sam3Labels?: string[];
    /** Optional custom labels from older OWLv2 call sites; folded into Rapid labels. */
    owlv2Labels?: string[];
    /** Optional confidence threshold for Rapid inference. */
    sam3ConfidenceThreshold?: number;
}

interface InferenceContext {
    mediaItem: MediaItem;
    extractedFrameMeta?: { videoId: string; segment: number };
    isDraft: boolean;
    abortController?: AbortController;
    sam3Labels: string[];
    sam3ConfidenceThreshold?: number;
    boxPromptingData: any[];
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determines which labels should be sent to Rapid text prompting.
 * If custom labels are provided, uses those. Otherwise uses every prompt label.
 */
function resolveLabels(
    promptLabels: Map<string, any>,
    customSam3Labels?: string[],
    customOwlv2Labels?: string[]
): { sam3Labels: string[]; owlv2Labels: string[] } {
    if (customSam3Labels !== undefined || customOwlv2Labels !== undefined) {
        return {
            sam3Labels: Array.from(
                new Set([...(customSam3Labels ?? []), ...(customOwlv2Labels ?? [])])
            ),
            owlv2Labels: customOwlv2Labels ?? []
        };
    }

    return { sam3Labels: Array.from(promptLabels.keys()), owlv2Labels: [] };
}

/**
 * Builds a map of media dimensions for all frames that have annotations.
 */
function buildMediaDimensionsMap(
    annotationsForFileAndFrameId: AnnotationsForFileAndFrameId,
    mediaStore: Record<string, MediaItem>,
    defaultDimensions?: { width: number; height: number }
): Map<string, { width: number; height: number }> {
    const dimensions = new Map<string, { width: number; height: number }>();

    Object.entries(annotationsForFileAndFrameId).forEach(([fileId, frames]) => {
        Object.keys(frames).forEach((frameId) => {
            if (dimensions.has(frameId)) return;

            const mediaItem = mediaStore[fileId];
            const itemDimensions =
                mediaItem?.metadata?.naturalDimensions ||
                mediaItem?.metadata?.serverMediaDimensions ||
                defaultDimensions;

            if (itemDimensions) {
                dimensions.set(frameId, itemDimensions);
            }
        });
    });

    return dimensions;
}

function resolveRapidPreviewConfidence(confidence?: number): number {
    return Math.max(
        RAPID_PREVIEW_MIN_CONFIDENCE_THRESHOLD,
        confidence ?? RAPID_PREVIEW_MIN_CONFIDENCE_THRESHOLD
    );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Centralized hook for running Rapid inference on media items.
 * Rapid accepts text prompts and user-created/user-edited box prompts in one request.
 *
 * @example
 * const { runInference } = useRunInference();
 *
 * // Simple usage - sends every prompt label plus any user box prompts
 * await runInference({
 *     mediaItem,
 *     annotationsForFileAndFrameId,
 *     isDraft: true
 * });
 *
 * // With custom labels (e.g., from inference strategy)
 * await runInference({
 *     mediaItem,
 *     annotationsForFileAndFrameId,
 *     sam3Labels: ['backpack', 'person'],
 *     owlv2Labels: ['car']
 * });
 */
export const useRunInference = () => {
    const { getPredictionsForFile } = useGetPredictions();
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceUrl = currentWorkspace?.url ?? "anonymous";
    const media = useMediaStore();
    const promptLabels = usePromptLabels();
    const addToInferenceQueue = useAddToInferenceQueue();
    const updateInferenceQueueItemStatus = useUpdateInferenceQueueItemStatus();

    /**
     * Starts the inference queue for tracking loading state.
     */
    const startInferenceQueue = useCallback(
        (mediaId: string, abortController?: AbortController) => {
            const queueItem: InferenceQueueItem = {
                id: mediaId,
                status: InferenceStatus.INFERRING,
                promise: Promise.resolve(),
                abortController,
                progress: 0
            };
            addToInferenceQueue(queueItem);
        },
        [addToInferenceQueue]
    );

    /**
     * Marks the inference queue item as completed.
     */
    const completeInferenceQueue = useCallback(
        (mediaId: string) => {
            updateInferenceQueueItemStatus(mediaId, InferenceStatus.COMPLETED);
        },
        [updateInferenceQueueItemStatus]
    );

    /**
     * Runs Rapid inference, which combines optional text prompts and optional box prompts.
     */
    const runRapidInference = useCallback(
        (ctx: InferenceContext): Promise<void> => {
            return getPredictionsForFile({
                mediaItem: ctx.mediaItem,
                extractedFrameMeta: ctx.extractedFrameMeta,
                inferenceType: InferenceTypes.RAPID,
                textPrompts: ctx.sam3Labels,
                customPromptingData: ctx.boxPromptingData,
                isDraft: ctx.isDraft,
                appendMode: false,
                useInferenceQueue: false,
                abortController: ctx.abortController,
                confidenceThreshold: resolveRapidPreviewConfidence(ctx.sam3ConfidenceThreshold)
            });
        },
        [getPredictionsForFile]
    );

    /**
     * Main inference runner.
     */
    const runInference = useCallback(
        async ({
            mediaItem,
            extractedFrameMeta,
            isDraft = false,
            annotationsForFileAndFrameId,
            abortController,
            sam3Labels: customSam3Labels,
            owlv2Labels: customOwlv2Labels,
            sam3ConfidenceThreshold
        }: RunInferenceParams): Promise<void> => {
            // Resolve all labels that should be sent as Rapid text prompts.
            const { sam3Labels, owlv2Labels } = resolveLabels(
                promptLabels,
                customSam3Labels,
                customOwlv2Labels
            );

            // Build dimensions map for box prompting
            const defaultDimensions =
                mediaItem.metadata?.naturalDimensions || mediaItem.metadata?.serverMediaDimensions;
            const mediaDimensions = buildMediaDimensionsMap(
                annotationsForFileAndFrameId,
                media,
                defaultDimensions
            );

            const rapidLabels = Array.from(new Set([...sam3Labels, ...owlv2Labels]));

            // Build box prompting data for any Rapid label that has user annotations
            const { promptingData: boxPromptingData } = buildBoxPromptingDataForLabels(
                annotationsForFileAndFrameId,
                rapidLabels,
                mediaDimensions,
                workspaceUrl
            );

            const hasTextPrompts = rapidLabels.length > 0;
            const hasBoxPrompts = boxPromptingData.length > 0;

            if (!hasTextPrompts && !hasBoxPrompts) {
                return;
            }

            const promptFrameIds = getPromptFrameIds(boxPromptingData);
            if (promptFrameIds.length > 0) {
                try {
                    await waitForPromptFrameUploads(
                        promptFrameIds,
                        () => useStore.getState().media
                    );
                } catch {
                    // Continue anyway - frames might be images that don't need upload
                }
            }

            const inferenceConfidenceThreshold =
                resolveRapidPreviewConfidence(sam3ConfidenceThreshold);

            // Build inference context
            const ctx: InferenceContext = {
                mediaItem,
                extractedFrameMeta,
                isDraft,
                abortController,
                sam3Labels: rapidLabels,
                sam3ConfidenceThreshold: inferenceConfidenceThreshold,
                boxPromptingData
            };

            // Start tracking in inference queue
            if (mediaItem.id) {
                startInferenceQueue(mediaItem.id, abortController);
            }

            await runRapidInference(ctx);

            // Mark as completed
            if (mediaItem.id) {
                completeInferenceQueue(mediaItem.id);
            }
        },
        [
            promptLabels,
            media,
            workspaceUrl,
            startInferenceQueue,
            completeInferenceQueue,
            runRapidInference
        ]
    );

    return { runInference };
};
